/**
 * XT-828 — the Guide and Transport a Mini Planner day adds for its
 * experiences, and what they cost:
 *
 * - one experience: a Transport and a Guide;
 * - two: a Guide each, and the Transport is a half day when the two last
 *   4 hours or less together, else a full day;
 * - three or more: a Guide each and a full-day Transport.
 *
 * The Transport hangs off the day's first experience. A line the agent
 * removed stays removed. Prices come from the organisation's Default pricing
 * (active rows, the country's value else the default), cost plus the matching
 * markup as XT applies it to a subitem; a price not set costs €200.
 *
 * XT-145 — a Transport is priced per VEHICLE, not as one number. The day's
 * largest group picks the vehicle (`plannerVehicle`), and the supplier's own
 * rate for that vehicle in that country wins when one is imported
 * (`transportCells`); Default pricing is what a day falls back to when the
 * supplier tables do not cover it.
 *
 * T-326 — an experience that brings its own vehicle (tuk-tuk, sidecar, jeep,
 * segway, bike) or goes on foot needs no car: the Transport counts only the
 * day's other experiences, and the agent can set its half or full day.
 */
import type { TransportRateLookupRow } from '../pricing/transport-rate-sheet';
import type { FxRates } from './fx-convert';
import {
  lookupTransportRouteRate,
  lookupTransportVehicleRate,
} from '../pricing/transport-rate-sheet';
import { convertAmount } from './fx-convert';

export const HALF_DAY_LIMIT_MINUTES = 240;
export const PLANNER_SERVICE_FALLBACK_COST = 200;

export type PlannerServiceKind = 'guide' | 'transport';
export type PlannerServiceSpan = 'half' | 'full';

export type PlannerServiceItem = {
  id: string;
  kind: string;
  durationMinutes?: number | null;
  /** The agent removed this experience's Guide. */
  guideRemoved?: boolean;
  /** The product's title: names an experience that needs no car. */
  title?: string | null;
};

export type PlannerDayService = {
  kind: PlannerServiceKind;
  /** The experience the line sits under. */
  itemId: string;
  /** Null for the lone experience's Transport, which has no half/full day. */
  span: PlannerServiceSpan | null;
  /**
   * A Transport the agent picked the vehicles for: this line is one car of
   * that type. Absent: the suggested vehicle (`plannerVehicle`).
   */
  vehicle?: string;
  /** The car's position among the day's (0-based); keys it in XT. */
  unit?: number;
  /** Seats of the cars before it: they carry the group first. */
  seatsBefore?: number;
};

/** The agent's pick for a day's Transport: `count` cars of `type`. */
export type PlannerTransportVehicle = { type: string; count: number };

/** At most this many cars on one day's Transport. */
export const PLANNER_MAX_TRANSPORT_UNITS = 10;

const knownMinutes = (item: PlannerServiceItem): number | null =>
  typeof item.durationMinutes === 'number' && item.durationMinutes > 0
    ? item.durationMinutes
    : null;

/** A tour of 4 hours or less needs a half-day guide; unknown is a full day. */
export const guideSpan = (item: PlannerServiceItem): PlannerServiceSpan => {
  const minutes = knownMinutes(item);
  return minutes != null && minutes <= HALF_DAY_LIMIT_MINUTES ? 'half' : 'full';
};

const OWN_TRANSPORT =
  /\btuk[\s-]?tuks?\b|\bsidecars?\b|\bjeeps?\b|\bsegways?\b|\be-?bikes?\b|\bbikes?\b|\bbicycles?\b|\bcycling\b|\bwalk(?:s|ing)?\b|\bon\s+foot\b/i;

/** An experience on its own vehicle or on foot: the day's car skips it. */
export const tourNeedsCar = (item: PlannerServiceItem): boolean =>
  !OWN_TRANSPORT.test(item.title ?? '');

const transportSpan = (
  tours: readonly PlannerServiceItem[]
): PlannerServiceSpan | null => {
  if (tours.length === 1) return null;
  if (tours.length > 2) return 'full';
  const [first, second] = tours.map(knownMinutes);
  return first != null &&
    second != null &&
    first + second <= HALF_DAY_LIMIT_MINUTES
    ? 'half'
    : 'full';
};

/** The picked vehicles as one entry per car, unknown types and zeros dropped. */
export const plannerTransportUnits = (
  vehicles: readonly PlannerTransportVehicle[] | null | undefined
): { type: string; seats: number }[] => {
  const units: { type: string; seats: number }[] = [];
  for (const entry of vehicles ?? []) {
    const known = plannerVehicleOf(entry.type);
    if (!known) continue;
    const count = Math.max(0, Math.floor(entry.count));
    for (let i = 0; i < count; i += 1) {
      if (units.length >= PLANNER_MAX_TRANSPORT_UNITS) return units;
      units.push({ type: known.type, seats: known.seats });
    }
  }
  return units;
};

/** The day's Guide and Transport lines, in day order. */
export const plannerDayServices = (
  items: readonly PlannerServiceItem[],
  options: {
    transportRemoved?: boolean;
    /** The agent's vehicles (one Transport line per car); none: suggested. */
    transportVehicles?: readonly PlannerTransportVehicle[] | null;
    /**
     * The agent's half or full day for a day's Transport; on a day whose
     * experiences need no car, the Transport the agent added.
     */
    transportSpan?: PlannerServiceSpan | null;
  } = {}
): PlannerDayService[] => {
  const tours = items.filter((item) => item.kind === 'experience');
  if (tours.length === 0) return [];
  const carTours = tours.filter(tourNeedsCar);
  // A lone experience's car takes it from the hotel (no half or full day).
  const span =
    carTours.length === 1
      ? null
      : (options.transportSpan ??
        (carTours.length > 0 ? transportSpan(carTours) : null));
  const first = carTours[0] ?? (span ? tours[0] : undefined);
  const services: PlannerDayService[] = [];
  for (const tour of tours) {
    if (tour === first && !options.transportRemoved) {
      const units = plannerTransportUnits(options.transportVehicles);
      if (units.length === 0) {
        services.push({ kind: 'transport', itemId: first.id, span });
      }
      let seatsBefore = 0;
      units.forEach((unit, index) => {
        services.push({
          kind: 'transport',
          itemId: first.id,
          span,
          vehicle: unit.type,
          unit: index,
          seatsBefore,
        });
        seatsBefore += unit.seats;
      });
    }
    if (!tour.guideRemoved) {
      services.push({ kind: 'guide', itemId: tour.id, span: guideSpan(tour) });
    }
  }
  return services;
};

/** "Guide", "Transport", "Transport · half day". */
export const plannerServiceLabel = (service: PlannerDayService): string => {
  const title = service.kind === 'guide' ? 'Guide' : 'Transport';
  if (service.kind === 'guide' || !service.span) return title;
  return `${title} · ${service.span === 'half' ? 'half day' : 'full day'}`;
};

// ---------------------------------------------------------------------------
// Prices

export type PlannerPricingRow = {
  property: string;
  default: number;
  countryValues: ReadonlyArray<{
    country?: string | null;
    value?: number | null;
  }>;
};

export type PlannerServiceRate = {
  /** What the DMC pays — the price XT stores as the subitem's cost. */
  cost: number;
  /** The Default pricing markup %, 0 when none. */
  markup: number;
  /** Cost plus markup: the line's price in the estimate. */
  price: number;
};

export type PlannerServiceRates = {
  guideHalf: PlannerServiceRate;
  guideFull: PlannerServiceRate;
  /** The Default-pricing Transport rate, and the fallback for every day. */
  transport: PlannerServiceRate;
  /** The country these rates are for; what supplier cells must match. */
  country: string | null;
  /**
   * Imported supplier cells (XT-828), most specific first: the chapter
   * region's sheets, then the country's default sheets (no region).
   */
  transportCells: readonly TransportRateLookupRow[];
  countryTransportCells: readonly TransportRateLookupRow[];
  /** The member's currency the Default pricing is in; cells convert to it. */
  currency: string | null;
  /** Rates for converting a cell in another currency; none: such a cell is skipped. */
  fx: FxRates | null;
};

/** Where a one-experience day's car goes: the hotel to the experience (towns). */
export type PlannerTransportRoute = {
  from: string | null | undefined;
  to: string | null | undefined;
};

const valueFor = (
  rows: readonly PlannerPricingRow[],
  property: string,
  country: string | null | undefined
): number | null => {
  const row = rows.find((entry) => entry.property === property);
  if (!row) return null;
  const match = country
    ? row.countryValues.find(
        (entry) => entry.country?.toLowerCase() === country.toLowerCase()
      )
    : undefined;
  const value = match?.value ?? row.default;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

const round2 = (value: number): number =>
  Math.round((value + Number.EPSILON) * 100) / 100;

const rate = (
  rows: readonly PlannerPricingRow[],
  priceProperty: string,
  markupProperty: string,
  country: string | null | undefined
): PlannerServiceRate => {
  const cost =
    valueFor(rows, priceProperty, country) ?? PLANNER_SERVICE_FALLBACK_COST;
  const markup = valueFor(rows, markupProperty, country) ?? 0;
  return { cost, markup, price: round2(cost * (1 + markup / 100)) };
};

/**
 * The country's Guide and Transport rates from the active pricing rows, plus
 * the supplier cells (XT-145) that can price a Transport per vehicle.
 */
export const plannerServiceRates = (
  rows: readonly PlannerPricingRow[],
  country: string | null | undefined,
  transportCells: readonly TransportRateLookupRow[] = [],
  options: {
    /** The chapter's planner region; its sheets alone price the day. */
    regionId?: string | null;
    currency?: string | null;
    fx?: FxRates | null;
  } = {}
): PlannerServiceRates => ({
  guideHalf: rate(rows, 'guide_price_hd', 'guide_markup_hd', country),
  guideFull: rate(rows, 'guide_price_fd', 'guide_markup_fd', country),
  transport: rate(rows, 'transport_price', 'transport_markup', country),
  country: country ?? null,
  transportCells: options.regionId
    ? transportCells.filter((cell) =>
        cell.regionIds.includes(options.regionId as string)
      )
    : [],
  countryTransportCells: transportCells.filter(
    (cell) => cell.regionIds.length === 0
  ),
  currency: options.currency ?? null,
  fx: options.fx ?? null,
});

/**
 * A day's Transport from the chapter region's supplier cells (XT-828), else
 * the country's default sheets (no region), else the Default-pricing
 * Transport rate:
 *
 * - one experience (`span` null — the car takes it from the hotel): its route,
 *   hotel → experience, else the region's half-day price;
 * - two or more (a half or full day of the car, `plannerDayServices`): the
 *   region's half- or full-day price — never a single route's.
 *
 * The vehicle seats `pax`; the cell must be valid on `onDate`. A cell in
 * another currency is converted to the member's, and skipped without a rate.
 */
export const plannerTransportRate = (
  rates: PlannerServiceRates,
  query: {
    pax: number;
    span: PlannerServiceSpan | null;
    onDate?: string | null;
    route?: PlannerTransportRoute | null;
    /** The car the agent picked; else the one that seats `pax`. */
    vehicle?: string | null;
  }
): PlannerServiceRate => {
  const picked = plannerVehicleOf(query.vehicle);
  const base = {
    country: rates.country,
    vehicle: picked?.type ?? plannerVehicle(query.pax).type,
    pax: query.pax,
    onDate: query.onDate,
    seats: picked?.seats ?? null,
  };
  const from = query.route?.from?.trim();
  const to = query.route?.to?.trim();
  const priceFrom = (cells: readonly TransportRateLookupRow[]) =>
    query.span === null
      ? ((from && to
          ? lookupTransportRouteRate(cells, { ...base, from, to })
          : null) ??
        lookupTransportVehicleRate(cells, { ...base, span: 'half' }))
      : lookupTransportVehicleRate(cells, { ...base, span: query.span });
  // The region's own table first, then the country's default one.
  const hit =
    priceFrom(rates.transportCells) ?? priceFrom(rates.countryTransportCells);
  return plannerCellRate(rates, hit);
};

/**
 * A supplier cell's cost as a planner line: converted to the member's
 * currency, with the org's Transport markup on top as on the Default-pricing
 * fallback. No cell, or no rate to convert it: the Default-pricing Transport.
 */
export const plannerCellRate = (
  rates: PlannerServiceRates,
  hit: { cost: number; currency: string } | null
): PlannerServiceRate => {
  if (!hit) return rates.transport;
  const cost =
    rates.currency == null
      ? hit.cost
      : convertAmount(hit.cost, hit.currency, rates.currency, rates.fx);
  if (cost == null) return rates.transport;
  const markup = rates.transport.markup;
  return {
    cost: round2(cost),
    markup,
    price: round2(cost * (1 + markup / 100)),
  };
};

/**
 * What one Guide or Transport line costs. A Transport priced per vehicle
 * (XT-145) needs the day's party, so callers that know it pass `pax`; without
 * it the line falls back to the Default-pricing rate.
 */
export const plannerServiceRate = (
  service: PlannerDayService,
  rates: PlannerServiceRates,
  day?: {
    pax?: number | null;
    /** The day's date (`YYYY-MM-DD`): a sheet out of validity prices nothing. */
    onDate?: string | null;
    /** A one-experience day's route, hotel → experience (XT-828). */
    route?: PlannerTransportRoute | null;
  }
): PlannerServiceRate => {
  if (service.kind === 'transport') {
    return day?.pax
      ? plannerTransportRate(rates, {
          pax: plannerUnitPax(service, day.pax),
          span: service.span,
          onDate: day.onDate,
          route: day.route,
          vehicle: service.vehicle ?? null,
        })
      : rates.transport;
  }
  return service.span === 'half' ? rates.guideHalf : rates.guideFull;
};

/**
 * The car's party (XT-840): each experience's own guests when it has them,
 * else the whole party, floored at one. The estimate and the Transport line
 * both size the car with this, so a day's total never disagrees with the
 * number printed next to it.
 */
export const plannerDayGroupSize = (
  items: ReadonlyArray<{
    guests?: { adults: number; children: number } | null;
  }>,
  party: { adults: number; children: number }
): number =>
  Math.max(
    1,
    ...items.map((item) =>
      item.guests
        ? item.guests.adults + item.guests.children
        : party.adults + party.children
    )
  );

export type PlannerVehicleType = {
  type: string;
  seats: number;
  /** XT's transport type for the car (driver products, booked lines). */
  transportType: string;
};

// XT-840 — the day's vehicle: the smallest that seats the day's largest
// group. Seats come from the vehicle type until transport products carry a
// capacity; both minibus sizes are XT's `Minibus` (XT-922).
export const PLANNER_VEHICLES: ReadonlyArray<PlannerVehicleType> = [
  { type: 'Sedan', seats: 2, transportType: 'Sedan' },
  { type: 'Minivan', seats: 6, transportType: 'Minivan' },
  { type: 'Sprinter', seats: 7, transportType: 'Sprinter' },
  { type: 'Minibus 19', seats: 19, transportType: 'Minibus' },
  { type: 'Minibus 29', seats: 29, transportType: 'Minibus' },
  { type: 'Bus', seats: 50, transportType: 'Bus' },
];

/**
 * The planner vehicle named `type`, else the largest of that XT transport
 * type — a draft saved with plain `Minibus` reads as the Minibus 29.
 */
export const plannerVehicleOf = (
  type: string | null | undefined
): PlannerVehicleType | undefined => {
  const wanted = type?.trim().toLowerCase();
  if (!wanted) return undefined;
  return (
    PLANNER_VEHICLES.find((vehicle) => vehicle.type.toLowerCase() === wanted) ??
    PLANNER_VEHICLES.filter(
      (vehicle) => vehicle.transportType.toLowerCase() === wanted
    ).at(-1)
  );
};

/**
 * The travellers one picked car carries: the cars fill in order, each up to
 * its seats, and a spare car still counts one. The suggested car takes all.
 */
export const plannerUnitPax = (
  service: PlannerDayService,
  group: number
): number => {
  if (!service.vehicle) return group;
  const seats = plannerVehicleOf(service.vehicle)?.seats ?? group;
  return Math.max(1, Math.min(seats, group - (service.seatsBefore ?? 0)));
};

/** Seats on a day's Transport: the picked cars', else the suggested one's. */
export const plannerTransportSeats = (
  vehicles: readonly PlannerTransportVehicle[] | null | undefined,
  group: number
): { seats: number; fits: boolean } => {
  const units = plannerTransportUnits(vehicles);
  const seats =
    units.length > 0
      ? units.reduce((sum, unit) => sum + unit.seats, 0)
      : plannerVehicle(group).seats;
  return { seats, fits: seats >= group };
};

/** The vehicle for `pax` travellers; the largest when none seats them. */
export const plannerVehicle = (
  pax: number
): { type: string; seats: number; fits: boolean } => {
  const fitting = PLANNER_VEHICLES.find((vehicle) => vehicle.seats >= pax);
  const vehicle = fitting ?? (PLANNER_VEHICLES.at(-1) as PlannerVehicleType);
  return { type: vehicle.type, seats: vehicle.seats, fits: Boolean(fitting) };
};

/** The `YYYY-MM-DD` of the trip's day `dayIndex` (0-based); null undated. */
export const plannerDayDate = (
  startOn: string | null | undefined,
  dayIndex: number
): string | null => {
  const match = /^\d{4}-\d{2}-\d{2}/.exec(startOn ?? '');
  if (!match) return null;
  const day = new Date(`${match[0]}T00:00:00Z`);
  day.setUTCDate(day.getUTCDate() + dayIndex);
  return day.toISOString().slice(0, 10);
};

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** `Sat 11 Jul` for the trip's day at `dayIndex`; null without a start. */
export const plannerDayDateLabel = (
  startOn: string | null | undefined,
  dayIndex: number
): string | null => {
  const date = plannerDayDate(startOn, dayIndex);
  if (!date) return null;
  const day = new Date(`${date}T00:00:00Z`);
  return `${WEEKDAYS[day.getUTCDay()]} ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]}`;
};

/** Whether a planner day is the trip's departure day (no hotel night). */
export const isPlannerDepartureDay = (day: unknown): boolean =>
  (day as { departure?: unknown } | null)?.departure === true;

/**
 * How many nights a planner draft holds: its days across chapters, but the
 * departure day.
 */
export const plannerDraftNightCount = (legs: unknown): number =>
  Array.isArray(legs)
    ? legs.reduce<number>((total, leg) => {
        const days = (leg as { days?: unknown } | null)?.days;
        return (
          total +
          (Array.isArray(days)
            ? days.filter((day) => !isPlannerDepartureDay(day)).length
            : 0)
        );
      }, 0)
    : 0;

/**
 * The trip's last date once it has nights: it ends the morning after its
 * last night — the departure day (11 Jul + 3 nights → 14 Jul). Null with no
 * start or no nights, where the brief's own end date stands.
 */
export const plannerTripFinishOn = (
  startOn: string | null | undefined,
  nights: number
): string | null => (nights > 0 ? plannerDayDate(startOn, nights) : null);

type RoutePlace = { id?: string; kind?: string; subtitle?: string | null };

/**
 * A one-experience day's route (XT-828): the chapter's hotel — its base
 * hotel, else the first hotel on its days — to the experience the Transport
 * is for, each by town.
 */
export const plannerTransportRoute = (
  leg: {
    baseHotel?: RoutePlace | null;
    days?: ReadonlyArray<{ items?: readonly RoutePlace[] }>;
  },
  dayItems: readonly RoutePlace[],
  itemId: string
): PlannerTransportRoute => ({
  from: plannerChapterHotel(leg)?.subtitle ?? null,
  to: dayItems.find((item) => item.id === itemId)?.subtitle ?? null,
});

/** The chapter's hotel: its base hotel, else the first hotel on its days. */
export const plannerChapterHotel = (leg: {
  baseHotel?: RoutePlace | null;
  days?: ReadonlyArray<{ items?: readonly RoutePlace[] }>;
}): RoutePlace | undefined =>
  leg.baseHotel ??
  (leg.days ?? [])
    .flatMap((day) => day.items ?? [])
    .find((item) => item.kind === 'hotel');
